import AssignmentIndOutlined from 'components/material-symbol/icons/AssignmentInd'
import CloseIcon from 'components/material-symbol/icons/Close'
import SendIcon from 'components/material-symbol/icons/Send'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Dialog from 'components/dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { yupResolver } from '@hookform/resolvers'
import { useAuth } from 'lib/auth'
import { gql, useMutation } from '@apollo/client'
import { useState } from 'react'
import { styled } from '@material-ui/core'
import { useDialogTitleStyles } from 'components/dialog/use-dialog-title-styles'
import { useRequirementDialogStyles } from './styles'

const REQUEST_BGS = gql`
  mutation NcsiBgsRequest(
    $firstName: String!
    $lastName: String!
    $email: String!
  ) {
    ncsiBgsRequest(firstName: $firstName, lastName: $lastName, email: $email) {
      success
      message
    }
  }
`

const validationSchema = yup.object().shape({
  firstName: yup
    .string()
    .min(1)
    .required(),
  lastName: yup
    .string()
    .min(1)
    .required(),
  email: yup
    .string()
    .email()
    .required(),
})

const formatErrorMessage = (name, message) => {
  if (!message) return null
  const error = message
    .split(' ')
    .slice(1)
    .join(' ')
  return `${name} ${error}`
}

const SuccessState = ({ classes, titleClasses }) => {
  const SuccessIcon = styled(AssignmentIndOutlined)(({ theme }) => ({
    borderRadius: '50%',
    background: 'rgba(0,25,75,.08)',
    padding: '1rem',
    color: theme.palette.primary.main,
    width: '83px',
    height: '83px',
    marginBottom: '.5rem',
  }))

  return (
    <DialogContent classes={{ root: titleClasses.content }}>
      <Box
        sx={{
          height: '290px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <SuccessIcon />
        <Typography align="center" variant="h6" className={classes.bodyCopy}>
          Form Submitted!
        </Typography>
        <Typography align="center" className={classes.bodyCopy}>
          You'll receive an email from NCSI with a link for your background
          screening.
        </Typography>
      </Box>
    </DialogContent>
  )
}

export default ({ isOpen, onClose }) => {
  const { user } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const [showSuccess, setShowSuccess] = useState(false)
  const titleClasses = useDialogTitleStyles()
  const classes = useRequirementDialogStyles()

  const [requestBackgroundScreen] = useMutation(REQUEST_BGS)

  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: user.coach?.email || '',
    },
    resolver: yupResolver(validationSchema),
  })

  const onSubmit = async formValues => {
    try {
      await requestBackgroundScreen({
        variables: { ...formValues },
      }).then(response => {
        if (response.data?.ncsiBgsRequest.success) {
          setShowSuccess(true)
        } else {
          enqueueSnackbar(response.data?.ncsiBgsRequest.message, {
            variant: 'error',
          })
        }
      })
    } catch (e) {
      enqueueSnackbar('Sorry, the request could not be sent', {
        variant: 'error',
      })
    }
  }

  const { handleSubmit, register, errors } = form

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      showCloseButton
      classes={{ paper: `${titleClasses.dialogPaper} ${classes.paper}` }}
    >
      {({ closeDialog }) => (
        <>
          <DialogTitle
            disableTypography
            classes={{ root: titleClasses.titleRoot }}
          >
            <Typography component="h2" className={titleClasses.title}>
              Background Screening Request
            </Typography>
          </DialogTitle>

          {showSuccess ? (
            <>
              <SuccessState classes={classes} titleClasses={titleClasses} />
              <DialogActions className={titleClasses.actions}>
                <Button
                  onClick={closeDialog}
                  color="primary"
                  variant="contained"
                  className={classes.pillButton}
                  startIcon={<CloseIcon fontSize="small" />}
                >
                  Close
                </Button>
              </DialogActions>
            </>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <DialogContent classes={{ root: classes.formContent }}>
                <Box>
                  <Typography className={classes.bodyCopy}>
                    Complete this form to send a background screen request to
                    NCSI. Once submitted, you will receive an email from NCSI
                    with a link to complete your background screening.
                  </Typography>
                  <Typography className={classes.bodyCopyEmphasis}>
                    Please enter your legal name as it should appear on your
                    background screening and the best email to contact you.
                  </Typography>
                </Box>
                <Box className={classes.formFields}>
                  <TextField
                    name="firstName"
                    inputRef={register}
                    fullWidth
                    variant="outlined"
                    label="First name *"
                    InputLabelProps={{ shrink: true }}
                    className={classes.formField}
                    error={!!errors.firstName}
                    helperText={
                      errors.firstName
                        ? formatErrorMessage(
                          'First name',
                          errors.firstName?.message,
                        )
                        : undefined
                    }
                  />
                  <TextField
                    name="lastName"
                    inputRef={register}
                    fullWidth
                    variant="outlined"
                    label="Last name *"
                    InputLabelProps={{ shrink: true }}
                    className={classes.formField}
                    error={!!errors.lastName}
                    helperText={
                      errors.lastName
                        ? formatErrorMessage(
                          'Last name',
                          errors.lastName?.message,
                        )
                        : undefined
                    }
                  />
                  <TextField
                    name="email"
                    inputRef={register}
                    fullWidth
                    variant="outlined"
                    label="Email *"
                    InputLabelProps={{ shrink: true }}
                    className={`${classes.formField} ${classes.formFieldFull}`}
                    error={!!errors.email}
                    helperText={
                      errors.email
                        ? formatErrorMessage('Email', errors.email?.message)
                        : undefined
                    }
                  />
                </Box>
              </DialogContent>

              <DialogActions className={titleClasses.actions}>
                <Button
                  aria-label="cancel"
                  color="primary"
                  onClick={closeDialog}
                >
                  Cancel
                </Button>
                <Button
                  aria-label="send-request"
                  color="primary"
                  variant="contained"
                  type="submit"
                  className={classes.pillButton}
                  startIcon={<SendIcon fontSize="small" />}
                >
                  Send
                </Button>
              </DialogActions>
            </form>
          )}
        </>
      )}
    </Dialog>
  )
}
